import { memo, useState, useEffect } from 'react';
import { gql } from '@apollo/client';
import { useIntl } from 'react-intl';
import { CheckmarkIcon, SearchField, ErrorAlertIcon } from '@library';
import type { MotorcycleLicensePlateLookupResults } from '../../types';
import { ProgressSpinner } from '../../../core/components/ProgressIndicators';
import useLoginState from '../../../core/hooks/useLoginState';
import useImmutableCallback from '../../../core/hooks/useImmutableCallback';
import useEffectWithoutMount from '../../../core/util/useEffectWithoutMount';
import useCancelableQuery from '../../../core/hooks/apollo/useCancelableQuery';

const licensePlateQuery = gql`
    query lookupMotorcycleLicensePlate($licensePlateNumber: String) {
        lookupMotorcycleLicensePlate(licensePlateNumber: $licensePlateNumber) {
            manufacturerName
            typeAcceptance
            model
            manufacturingStartYear
            manufacturingEndYear
            registrationDate
        }
    }
`;

interface LicensePlateSearchProps {
    licensePlateNumber: string;
    setLicensePlateNumber: (value: string) => void;
    onLicensePlateResultUpdate(typeApprovalList?: string[]): void;
}

const LicensePlateSearch = (props: LicensePlateSearchProps) => {
    const isLoggedIn = useLoginState();

    if (!isLoggedIn) {
        return null;
    }

    const {
        licensePlateNumber,
        setLicensePlateNumber,
        onLicensePlateResultUpdate,
    } = props;

    return (
        <LicensePlateSearchContent
            licensePlateNumber={licensePlateNumber}
            setLicensePlateNumber={setLicensePlateNumber}
            onLicensePlateResultUpdate={onLicensePlateResultUpdate}
        />
    );
};

const LicensePlateValidity = memo(
    ({
        loading,
        isValidLicensePlate,
        length,
    }: {
        loading: boolean;
        isValidLicensePlate: boolean;
        length: number | undefined;
    }) => {
        if (length === 0) {
            return null;
        }
        if (loading) {
            return <ProgressSpinner />;
        }

        if (isValidLicensePlate) {
            return <CheckmarkIcon size={13} color="var(--color-success)" />;
        }

        return (
            <ErrorAlertIcon
                mini
                color="var( --color-danger)"
                data-test-class="plate-search-error"
            />
        );
    }
);

const LicensePlateSearchContent = memo(
    ({
        licensePlateNumber,
        setLicensePlateNumber,
        onLicensePlateResultUpdate,
    }: LicensePlateSearchProps) => {
        const intl = useIntl();

        const onLicensePlateResultUpdateImmutable = useImmutableCallback(
            onLicensePlateResultUpdate
        );
        const setLicensePlateNumberImmutable = useImmutableCallback(
            setLicensePlateNumber
        );

        const [value, setValue] = useState(licensePlateNumber);
        useEffectWithoutMount(() => {
            const handler = setTimeout(() => {
                setValue(licensePlateNumber);
            }, 500);

            return () => {
                clearTimeout(handler);
            };
        }, [licensePlateNumber]);

        const { data, error, loading } = useCancelableQuery<{
            lookupMotorcycleLicensePlate: MotorcycleLicensePlateLookupResults[];
        }>(licensePlateQuery, {
            fetchPolicy: 'no-cache',
            variables: { licensePlateNumber: value },
            skip: !value,
        });

        useEffect(() => {
            if (error) {
                onLicensePlateResultUpdateImmutable();
            }
            if (data?.lookupMotorcycleLicensePlate) {
                onLicensePlateResultUpdateImmutable(
                    data.lookupMotorcycleLicensePlate.map(
                        item => item.typeAcceptance
                    )
                );
            }
        }, [data, error, onLicensePlateResultUpdateImmutable]);

        const handleLicenseSearchChange = (newValue: string) => {
            setLicensePlateNumberImmutable(newValue.replace(/[^A-Z0-9]/gi, ''));
        };

        return (
            <div data-test-class="licenseSearch">
                <SearchField
                    light
                    data-test-class="license-search"
                    value={licensePlateNumber}
                    onChange={handleLicenseSearchChange}
                    rounded
                    placeholder={`${intl.formatMessage({
                        id: 'e.g.',
                    })}  LU56`}
                    endAdornment={
                        <LicensePlateValidity
                            loading={loading}
                            isValidLicensePlate={
                                !!data?.lookupMotorcycleLicensePlate.length &&
                                !!licensePlateNumber.length &&
                                !error
                            }
                            length={licensePlateNumber.length}
                        />
                    }
                />
            </div>
        );
    }
);

export default memo(LicensePlateSearch);
